/**
 * Weekly visit streak: consecutive ISO weeks with >=1 purchase, counting
 * backwards from the current week. An empty current week doesn't break the
 * streak (the customer still has until Sunday); a full missing week does.
 * Pure function — shared by the owner-insights API and the Pixel Pass.
 */

// ISO week key "2026-W28" for a date (UTC-based; good enough for streaks).
export function isoWeekKey(input: string | Date): string {
  const date = new Date(input);
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;          // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);  // nearest Thursday
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

function previousWeekKey(now: Date): string {
  const prev = new Date(now.getTime() - 7 * 86400000);
  return isoWeekKey(prev);
}

/**
 * Streak saver (041): the ONE repairable gap. Repair is offered only when
 * LAST week is empty but the week before it had a purchase — i.e. buying that
 * single week reconnects the customer's streak history. Older gaps are gone
 * for good (that's what makes streaks mean something).
 * Returns the repairable week key, or null.
 */
export function repairableWeek(dates: (string | Date)[], now: Date = new Date()): string | null {
  const weeks = new Set(dates.map(isoWeekKey));
  const lastWeek = new Date(now.getTime() - 7 * 86400000);
  const weekBefore = new Date(now.getTime() - 14 * 86400000);
  if (weeks.has(isoWeekKey(lastWeek))) return null;         // nothing broken
  if (!weeks.has(isoWeekKey(weekBefore))) return null;      // nothing to reconnect
  return isoWeekKey(lastWeek);
}

/** A concrete date inside the week `key` names (its Thursday) — lets callers
 * feed repaired weeks back into weeklyStreak as synthetic purchase dates. */
export function dateForWeekKey(key: string): Date {
  const m = key.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return new Date(NaN);
  const year = Number(m[1]);
  const week = Number(m[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4)); // always in ISO week 1
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Thursday = new Date(jan4.getTime() + (4 - jan4Day) * 86400000);
  return new Date(week1Thursday.getTime() + (week - 1) * 7 * 86400000);
}

export function weeklyStreak(dates: (string | Date)[], now: Date = new Date()): number {
  if (dates.length === 0) return 0;
  const weeks = new Set(dates.map(isoWeekKey));
  const currentKey = isoWeekKey(now);
  // Anchor: current week if it has a purchase, else last week (grace), else 0.
  let cursor: Date;
  if (weeks.has(currentKey)) {
    cursor = new Date(now);
  } else if (weeks.has(previousWeekKey(now))) {
    cursor = new Date(now.getTime() - 7 * 86400000);
  } else {
    return 0;
  }
  let streak = 0;
  while (weeks.has(isoWeekKey(cursor))) {
    streak += 1;
    cursor = new Date(cursor.getTime() - 7 * 86400000);
  }
  return streak;
}
